import CustomRadio from "@components/Forms/CustomRadio";
import { GenesisSyncIcon } from "@components/Icons";
import GenesisSyncInfoTooltip from "@components/UI/GenesisSyncInfoTooltip";
import Spin from "@components/UI/Spin";
import { GENESIS_SYNC_V2_MODULES, isGenesisV2Enabled } from "./genesisSyncModules";
import { useTheme } from "@hooks/useTheme";
import { formatDateTime } from "@utils/common";
import { useTranslation } from "react-i18next";
import Select from "react-select";

const getSyncSelectStyles = (colors, isLoading) => ({
  control: (base, state) => ({
    ...base,
    backgroundImage: "linear-gradient(to right, #1EC6FF, #367FFF)",
    border: "none",
    borderRadius: "8px",
    outline: "none",
    boxShadow: "none",
    minHeight: "36px",
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    opacity: state.isDisabled ? 0.7 : 1,
    "&:hover": {
      border: "none",
      outline: "none",
      boxShadow: "none",
    },
    "&:focus": {
      border: "none",
      outline: "none",
      boxShadow: "none",
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: "#000000",
    margin: 0,
    padding: 0,
    fontSize: "14px",
    fontWeight: "500",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#000000",
    fontWeight: "500",
    fontSize: "14px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: colors.bgPrimaryColor,
    color: state.isSelected || state.isFocused ? colors.textActiveColor : colors.textPrimaryColor,
    border:
      state.isSelected || state.isFocused
        ? `1px solid ${colors.dropdownBorderColor}`
        : "1px solid transparent",
    boxShadow:
      state.isSelected || state.isFocused ? "0px -0.1px 4px 1px rgba(0, 0, 0, 0.6)" : "none",
    borderRadius: "0.25rem",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    marginBottom: "3px",
    position: "relative",
    zIndex: state.isSelected || state.isFocused ? 1000 : 0,
    transition: "all 0.2s ease",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    "&:hover": {
      backgroundColor: colors.bgPrimaryColor,
      color: colors.textActiveColor,
    },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: colors.bgPrimaryColor,
    borderRadius: "0.5rem",
    marginTop: 4,
    boxShadow: "0px 0px 6px 1px rgba(0, 0, 0, 0.6)",
    padding: "0.125rem",
    zIndex: 50,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 50,
  }),
  menuList: (base) => ({
    ...base,
    borderRadius: "0.5rem",
    padding: "0.25rem",
    margin: 0,
    backgroundColor: colors.bgPrimaryColor,
    color: colors.textPrimaryColor,
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "#000000",
    paddingRight: 8,
    "&:hover": {
      color: "#000000",
    },
  }),
  indicatorsContainer: (base) => ({
    ...base,
    display: isLoading ? "none" : "flex",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 12px",
  }),
});

const StatusBadge = ({ status, colors, t }) => {
  const normalized = status || "IDLE";
  const statusStyles = {
    RUNNING: colors.importStatusInProgress,
    SUCCESS: colors.importStatusCreated,
    FAILED: colors.importStatusFailed,
    IDLE: colors.importStatusIdle,
  };

  const displayText =
    normalized === "RUNNING"
      ? t("preferences.genesis-sync-status-running", "Running")
      : normalized === "SUCCESS"
        ? t("preferences.genesis-sync-status-success", "Success")
        : normalized === "FAILED"
          ? t("preferences.genesis-sync-status-failed", "Failed")
          : t("preferences.genesis-sync-status-idle", "Idle");

  return (
    <span
      className={`inline-flex items-center rounded-2xl px-2 py-1 text-sm leading-[16px] font-semibold ${statusStyles[normalized] || colors.badgeVariantDefault
        }`}
    >
      {displayText}
    </span>
  );
};

const VersionBadge = ({ isV2, colors, t }) => (
  <span
    className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${colors.bgMailTypeExternal} ${colors.textCompletionRequestDate}`}
  >
    {isV2
      ? t("preferences.genesis-sync-v2", "Genesis Sync v2")
      : t("preferences.genesis-sync-v1", "Genesis Sync v1")}
  </span>
);

const SyncDropdown = ({
  isLoading,
  disabled,
  title,
  options,
  onChange,
  menuPortalTarget,
  colors,
  t,
  placeholder,
}) => (
  <div className="relative w-30 flex-shrink-0" title={title}>
    <Select
      options={options}
      value={null}
      onChange={onChange}
      placeholder={
        isLoading
          ? t("preferences.genesis-sync-loading", "Syncing...")
          : placeholder || t("preferences.genesis-sync-actions", "Sync")
      }
      isSearchable={false}
      styles={getSyncSelectStyles(colors, isLoading)}
      isClearable={false}
      isDisabled={disabled || isLoading}
      menuPortalTarget={menuPortalTarget || undefined}
      menuPosition={menuPortalTarget ? "fixed" : "absolute"}
    />
    {isLoading && (
      <div className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 ${colors.textActive}`}>
        <Spin />
      </div>
    )}
  </div>
);

/**
 * Preferences → Genesis Sync section (edit radios + view v1/v2).
 * Sync mutations / confirm stay in PreferencesList; this is presentation + local select UX.
 */
export default function GenesisSyncSection({
  isEditingMode,
  editFormData,
  onInputChange,
  preferencesData,
  genesisSyncStatus,
  isGenesisSyncLoading,
  menuPortalTarget,
  onV1SyncSelect,
  onV2SyncSelect,
}) {
  const { t } = useTranslation();
  const { colors } = useTheme();

  const isV2 = isGenesisV2Enabled(preferencesData?.useGenesisV2?.codeValue);
  const editUseGenesisV2 = editFormData?.useGenesisV2 || "N";

  const syncOptions = [
    {
      value: "incremental",
      label: t("preferences.genesis-sync-incremental", "Sync Now"),
    },
    {
      value: "full",
      label: t("preferences.genesis-sync-full", "Full Sync"),
    },
  ];

  const comingSoon = t("preferences.genesis-sync-coming-soon", "Coming soon");

  return (
    <div
      className={`mb-4 rounded-2xl p-4 ${colors.bgPrimary} ${colors.borderPrimary}`}
      data-testid="genesis-sync-section"
    >
      <div className="flex flex-row h-full gap-6">
        <div className="w-1/5 flex-shrink-0 h-full p-4">
          <div className="flex flex-col items-start gap-3 h-full justify-start relative">
            <GenesisSyncIcon size={64} className={colors.textSecondary} />
            <div>
              <h3 className={`font-medium ${colors.textPrimary} uppercase`}>
                {t("preferences.genesis-sync-title", "Genesis Sync")}
              </h3>
              <p className={`text-xs ${colors.textSecondary} mt-1`}>
                {t(
                  "preferences.genesis-sync-section-description",
                  "Manage Genesis synchronization and related updates.",
                )}
              </p>
            </div>
          </div>
        </div>

        <div className={`block border-l -my-4 ${colors.dividerDashed}`}></div>

        <div className="w-4/5 h-full">
          <div className="space-y-4">
            {isEditingMode ? (
              <div className="flex items-center gap-4">
                <label className={`text-sm font-medium ${colors.textSecondary} w-44 flex-shrink-0`}>
                  {t("preferences.genesis-sync-version", "Genesis Sync Version")}
                </label>
                <div className="flex items-center gap-6">
                  <CustomRadio
                    name="useGenesisV2"
                    value="N"
                    checked={editUseGenesisV2 !== "Y"}
                    onChange={() => onInputChange("useGenesisV2", "N")}
                    label={t("preferences.genesis-sync-v1", "Genesis Sync v1")}
                  />
                  <CustomRadio
                    name="useGenesisV2"
                    value="Y"
                    checked={editUseGenesisV2 === "Y"}
                    onChange={() => onInputChange("useGenesisV2", "Y")}
                    label={t("preferences.genesis-sync-v2", "Genesis Sync v2")}
                  />
                </div>
              </div>
            ) : isV2 ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                  <div className="flex items-center gap-4">
                    <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                      {t("preferences.genesis-sync-version", "Genesis Sync Version")}
                    </label>
                    <div className="flex items-center min-h-[2.5rem] flex-1">
                      <VersionBadge isV2 colors={colors} t={t} />
                      <GenesisSyncInfoTooltip />
                    </div>
                  </div>
                </div>

                <p className={`text-sm ${colors.textSecondary}`}>
                  {t(
                    "preferences.genesis-sync-v2-hint",
                    "Synchronize specific data types independently.",
                  )}
                </p>

                <div className={`overflow-x-auto rounded-lg border ${colors.borderPrimary}`}>
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className={`${colors.bgSecondary}`}>
                        <th
                          className={`px-4 py-3 text-left font-medium ${colors.textSecondary}`}
                        >
                          {t("preferences.genesis-sync-col-module", "Module")}
                        </th>
                        <th
                          className={`px-4 py-3 text-left font-medium ${colors.textSecondary}`}
                        >
                          {t("preferences.genesis-sync-col-last-sync", "Last Sync")}
                        </th>
                        <th
                          className={`px-4 py-3 text-left font-medium ${colors.textSecondary}`}
                        >
                          {t("preferences.genesis-sync-col-status", "Status")}
                        </th>
                        <th
                          className={`px-4 py-3 text-left font-medium ${colors.textSecondary}`}
                        >
                          {t("preferences.genesis-sync-col-action", "Action")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {GENESIS_SYNC_V2_MODULES.map((module) => {
                        const streamStatus =
                          genesisSyncStatus?.streams?.[module.statusStreamKey] || {};
                        const rowStatus = streamStatus.status || "IDLE";
                        const rowLoading = rowStatus === "RUNNING";
                        const Icon = module.icon;
                        const actionDisabled = !module.enabled || rowLoading;

                        return (
                          <tr
                            key={module.id}
                            className={`border-t ${colors.borderPrimary}`}
                            data-testid={`genesis-sync-row-${module.id}`}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <Icon size={20} color={module.iconColor} />
                                <span className={colors.textPrimary}>
                                  {t(module.labelKey, module.labelDefault)}
                                </span>
                              </div>
                            </td>
                            <td className={`px-4 py-3 ${colors.textPrimary}`}>
                              {streamStatus.lastSyncAt
                                ? formatDateTime(streamStatus.lastSyncAt)
                                : "---"}
                            </td>
                            <td className="px-4 py-3">
                              <StatusBadge status={rowStatus} colors={colors} t={t} />
                            </td>
                            <td className="px-4 py-3">
                              <SyncDropdown
                                isLoading={rowLoading}
                                disabled={actionDisabled}
                                title={!module.enabled ? comingSoon : undefined}
                                options={syncOptions}
                                onChange={(option) => onV2SyncSelect?.(module, option)}
                                menuPortalTarget={menuPortalTarget}
                                colors={colors}
                                t={t}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                <div className="flex items-center gap-4">
                  <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                    {t("preferences.genesis-sync-version", "Genesis Sync Version")}
                  </label>
                  <div className="flex items-center min-h-[2.5rem] flex-1">
                    <VersionBadge isV2={false} colors={colors} t={t} />
                    <GenesisSyncInfoTooltip />
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                    {t("preferences.genesis-sync-status", "Last Sync Status")}
                  </label>
                  <div className="flex items-center gap-2 min-h-[2.5rem] flex-1">
                    <StatusBadge
                      status={genesisSyncStatus?.status}
                      colors={colors}
                      t={t}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                    {t("preferences.genesis-sync-title", "Genesis Sync")}
                  </label>
                  <SyncDropdown
                    isLoading={isGenesisSyncLoading}
                    disabled={isGenesisSyncLoading}
                    options={syncOptions}
                    onChange={onV1SyncSelect}
                    menuPortalTarget={menuPortalTarget}
                    colors={colors}
                    t={t}
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className={`text-sm font-medium ${colors.textSecondary} w-36 flex-shrink-0`}>
                    {t("preferences.genesis-last-sync-date", "Last Sync Date")}
                  </label>
                  <p
                    className={`text-sm ${colors.textPrimary} min-h-[2.5rem] flex items-center break-all flex-1`}
                  >
                    {genesisSyncStatus?.lastSyncAt
                      ? formatDateTime(genesisSyncStatus.lastSyncAt)
                      : "---"}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
